import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useBrand } from '../context/BrandContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import useUsageLimits from '../hooks/useUsageLimits';
import UsageBadge from '../components/UsageBadge';
import PaywallModal from '../components/PaywallModal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Sparkles, 
  Loader2, 
  Copy, 
  Check,
  FileText,
  Clock,
  Globe,
  Lock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const contentTypes = [
  { value: 'short', label: 'YouTube Short', duration: '60 sec', icon: '📱' },
  { value: 'reel', label: 'Instagram Reel', duration: '60 sec', icon: '📸' },
  { value: 'youtube_video', label: 'YouTube Video', duration: '8-10 min', icon: '🎬' },
  { value: 'instagram_post', label: 'Instagram Post', duration: 'Caption', icon: '📝' },
];

export default function ScriptGenerator() {
  const { currentBrand, brands } = useBrand();
  const [website, setWebsite] = useState(currentBrand.id);
  const [contentType, setContentType] = useState('short');
  const [topic, setTopic] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  const [generatedScript, setGeneratedScript] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const { limits, refresh: refreshLimits, isLocked } = useUsageLimits();
  const [showPaywall, setShowPaywall] = useState(false);
  const locked = isLocked('script');

  const handleGenerate = async () => {
    if (!topic.trim()) {
      toast.error('Please enter a topic');
      return;
    }
    if (locked) {
      setShowPaywall(true);
      toast.error('Free limit reached. Contact support to upgrade.');
      return;
    }

    setLoading(true);
    setGeneratedScript('');

    try {
      const response = await axios.post(
        `${API}/content/script`,
        {
          website,
          content_type: contentType,
          topic: topic.trim(),
          additional_context: additionalContext.trim() || null
        },
        { withCredentials: true }
      );
      setGeneratedScript(response.data.script);
      toast.success('Script generated successfully!');
      refreshLimits();
    } catch (error) {
      console.error('Script generation error:', error);
      const detail = error.response?.data?.detail;
      if (error.response?.status === 403 && detail && typeof detail === 'object' && detail.code === 'FREE_TIER_LIMIT_REACHED') {
        setShowPaywall(true);
        toast.error('Free limit reached. Contact support to upgrade.');
        refreshLimits();
      } else {
        const msg = typeof detail === 'string' ? detail : (detail?.message || 'Failed to generate script');
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(generatedScript);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const selectedContentType = contentTypes.find(ct => ct.value === contentType);
  const selectedBrand = brands[website];

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-3xl font-bold text-zinc-100 font-['Manrope']">Script Generator</h1>
        <p className="text-zinc-400 mt-1">Create engaging scripts for your social media content</p>
      </motion.div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Input Section */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-6"
        >
          <div className="p-6 rounded-xl bg-zinc-900/50 backdrop-blur-xl border border-zinc-800 space-y-6">
            {/* Website Selector */}
            <div className="space-y-2">
              <Label className="text-zinc-300 flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Website
              </Label>
              <Select value={website} onValueChange={setWebsite}>
                <SelectTrigger 
                  data-testid="website-select"
                  className="bg-zinc-900/50 border-zinc-800 h-12"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {Object.values(brands).map((brand) => (
                    <SelectItem key={brand.id} value={brand.id}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ background: brand.primary }}
                        />
                        {brand.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Content Type */}
            <div className="space-y-2">
              <Label className="text-zinc-300 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Content Type
              </Label>
              <Select value={contentType} onValueChange={setContentType}>
                <SelectTrigger 
                  data-testid="content-type-select"
                  className="bg-zinc-900/50 border-zinc-800 h-12"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {contentTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-3">
                        <span>{type.icon}</span>
                        <span>{type.label}</span>
                        <span className="text-xs text-zinc-500">({type.duration})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Topic */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Topic / Main Idea *</Label>
              <Textarea
                data-testid="topic-input"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="e.g., 5 ways AI voice agents can save your business money"
                className="bg-zinc-900/50 border-zinc-800 min-h-[100px] resize-none"
              />
            </div>

            {/* Additional Context */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Additional Context (Optional)</Label>
              <Textarea
                data-testid="context-input"
                value={additionalContext}
                onChange={(e) => setAdditionalContext(e.target.value)}
                placeholder="Any specific points, tone preferences, or target audience details..."
                className="bg-zinc-900/50 border-zinc-800 min-h-[80px] resize-none"
              />
            </div>

            {/* Generate Button */}
            <div className="space-y-2">
              <UsageBadge limits={limits} category="script" onUpgradeClick={() => setShowPaywall(true)} />
              <Button
                data-testid="generate-script-btn"
                onClick={handleGenerate}
                disabled={loading || !topic.trim() || locked}
                title={locked ? 'Free limit reached. Upgrade to Pro to continue.' : ''}
                className="w-full h-12 rounded-xl font-medium transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                style={ locked
                  ? { background: 'linear-gradient(135deg, #52525b 0%, #3f3f46 100%)' }
                  : { background: `linear-gradient(135deg, ${selectedBrand?.primary || currentBrand.primary} 0%, ${selectedBrand?.secondary || currentBrand.secondary} 100%)` }
                }
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Generating...
                  </span>
                ) : locked ? (
                  <span className="flex items-center gap-2">
                    <Lock className="w-5 h-5" />
                    Script limit reached — Upgrade
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    Generate Script
                  </span>
                )}
              </Button>
            </div>
          </div>

          {/* Info Card */}
          <div 
            className="p-4 rounded-xl border"
            style={{ 
              background: `${selectedBrand?.primary || currentBrand.primary}10`,
              borderColor: `${selectedBrand?.primary || currentBrand.primary}30`
            }}
          >
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 mt-0.5" style={{ color: selectedBrand?.primary || currentBrand.primary }} />
              <div>
                <p className="text-sm font-medium text-zinc-200">
                  {selectedContentType?.label} • {selectedContentType?.duration}
                </p>
                <p className="text-xs text-zinc-400 mt-1">
                  AI will generate a script optimized for {selectedBrand?.name || currentBrand.name}
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Output Section */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="p-6 rounded-xl bg-zinc-900/50 backdrop-blur-xl border border-zinc-800 h-full flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-zinc-100 font-['Manrope']">Generated Script</h2>
              {generatedScript && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCopy}
                  className="text-zinc-400 hover:text-zinc-200"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  <span className="ml-2">{copied ? 'Copied!' : 'Copy'}</span>
                </Button>
              )}
            </div>

            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <Loader2 
                    className="w-12 h-12 animate-spin mx-auto mb-4"
                    style={{ color: currentBrand.primary }}
                  />
                  <p className="text-zinc-400">Generating your script...</p>
                  <p className="text-sm text-zinc-500 mt-1">This may take 15-30 seconds</p>
                </div>
              </div>
            ) : generatedScript ? (
              <div className="flex-1 overflow-auto">
                <div className="prose prose-invert prose-sm max-w-none">
                  <pre className="whitespace-pre-wrap text-zinc-300 font-['JetBrains_Mono'] text-sm leading-relaxed bg-zinc-950/50 p-4 rounded-lg border border-zinc-800">
                    {generatedScript}
                  </pre>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center border-2 border-dashed border-zinc-800 rounded-xl">
                <div className="text-center p-8">
                  <FileText className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                  <p className="text-zinc-500">Your generated script will appear here</p>
                  <p className="text-sm text-zinc-600 mt-1">Fill in the form and click Generate</p>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      <PaywallModal
        open={showPaywall}
        onOpenChange={setShowPaywall}
        category="script"
        limit={limits?.script?.limit ?? 2}
      />
    </div>
  );
}
